/**
 * Stakeholder Pulse Store
 *
 * Manages stakeholder pulse records: outcome notifications sent to idea
 * submitters after a priority list is published, plus their responses.
 *
 * DB-first: all writes go to Supabase before local state updates.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { sendNotification } from '../../lib/notificationService';
import {
  rowToStakeholderPulse,
  type StakeholderPulse,
  type StakeholderPulseWithDetails,
  type PulseResponse,
} from '../../types/prioritization/stakeholderPulse';

const TABLE = 'stakeholder_pulses';

// ── State interface ───────────────────────────────────────────────────────────

export interface StakeholderPulseStoreState {
  pulses: StakeholderPulse[];
  isLoading: boolean;
  error: string | null;

  // ── Fetch ──────────────────────────────────────────────────
  /** Load all pulses for the current workspace (facilitator view). */
  fetchForWorkspace: () => Promise<void>;

  /** Load pulses for the current authenticated user (submitter view). */
  fetchForCurrentUser: (userId: string) => Promise<void>;

  /** Load pulses for a specific priority list (post-publish review). */
  fetchForPrioritySet: (prioritySetId: string) => Promise<void>;

  // ── Write ──────────────────────────────────────────────────
  /**
   * Mark a batch of pending pulses as sent (sets status → 'sent', sent_at = now).
   * Typically called from the facilitator "Send Pulses" action.
   */
  markSent: (pulseIds: string[]) => Promise<{ sentCount: number; error?: string }>;

  /**
   * Mark a pulse as viewed (sets status → 'viewed', viewed_at = now).
   * Called when a submitter opens their outcome notification.
   */
  markViewed: (pulseId: string) => Promise<void>;

  /**
   * Record a submitter's response to their pulse.
   */
  recordResponse: (
    pulseId: string,
    response: PulseResponse,
    responseNote?: string,
  ) => Promise<{ success: boolean; error?: string }>;

  // ── Queries ────────────────────────────────────────────────
  getPulseById: (id: string) => StakeholderPulse | undefined;
  getPulsesForPrioritySet: (prioritySetId: string) => StakeholderPulse[];
  getPulsesForSubmitter: (submitterId: string) => StakeholderPulse[];
  getPulsesForItem: (itemId: string) => StakeholderPulse[];

  /** Aggregate response-rate signal for a priority list. */
  getSignalForPrioritySet: (prioritySetId: string) => PulseSignal;
}

// ── Signal type ───────────────────────────────────────────────────────────────

export interface PulseSignal {
  total: number;
  sent: number;
  responded: number;
  addressed: number;
  partiallyAddressed: number;
  notAddressed: number;
  responseRate: number;       // 0–1
  addressedRate: number;      // addressed / responded (0–1)
  qualityScore: number;       // weighted: addressed=1, partial=0.5, not=0 (0–1)
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useStakeholderPulseStore = create<StakeholderPulseStoreState>((set, get) => ({
  pulses: [],
  isLoading: false,
  error: null,

  // ── Fetch ─────────────────────────────────────────────────────────────────

  fetchForWorkspace: async () => {
    if (!isSupabaseAvailable()) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;

    set({ isLoading: true, error: null });
    try {
      const { data, error } = await getSafeSupabaseClient()!
        .from(TABLE)
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      set({
        pulses: (data ?? []).map(rowToStakeholderPulse),
        isLoading: false,
      });
    } catch (err) {
      set({ error: String(err), isLoading: false });
    }
  },

  fetchForCurrentUser: async (userId) => {
    if (!isSupabaseAvailable()) return;

    set({ isLoading: true, error: null });
    try {
      const { data, error } = await getSafeSupabaseClient()!
        .from(TABLE)
        .select('*')
        .eq('submitter_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const incoming = (data ?? []).map(rowToStakeholderPulse);
      // Merge without clobbering workspace-fetched data
      set(state => {
        const existingIds = new Set(incoming.map(p => p.id));
        const kept = state.pulses.filter(p => !existingIds.has(p.id));
        return { pulses: [...kept, ...incoming], isLoading: false };
      });
    } catch (err) {
      set({ error: String(err), isLoading: false });
    }
  },

  fetchForPrioritySet: async (prioritySetId) => {
    if (!isSupabaseAvailable()) return;

    try {
      const { data, error } = await getSafeSupabaseClient()!
        .from(TABLE)
        .select('*')
        .eq('priority_set_id', prioritySetId)
        .order('rank', { ascending: true, nullsFirst: false });

      if (error) throw error;

      const incoming = (data ?? []).map(rowToStakeholderPulse);
      set(state => {
        const existingIds = new Set(incoming.map(p => p.id));
        const kept = state.pulses.filter(p => !existingIds.has(p.id));
        return { pulses: [...kept, ...incoming] };
      });
    } catch (err) {
      console.error('[StakeholderPulseStore] fetchForPrioritySet failed:', err);
    }
  },

  // ── Write ─────────────────────────────────────────────────────────────────

  markSent: async (pulseIds) => {
    if (!isSupabaseAvailable()) return { sentCount: 0, error: 'Supabase unavailable' };
    if (pulseIds.length === 0) return { sentCount: 0 };

    // Resolve the full pulse objects so we can deliver notifications with
    // the correct recipient and outcome summary.
    const pending = get().pulses.filter(
      p => pulseIds.includes(p.id) && p.status === 'pending',
    );
    if (pending.length === 0) return { sentCount: 0 };

    try {
      const now = new Date().toISOString();
      const { error } = await getSafeSupabaseClient()!
        .from(TABLE)
        .update({ status: 'sent', sent_at: now })
        .in('id', pending.map(p => p.id))
        .eq('status', 'pending');

      if (error) throw error;

      // Deliver a persistent in-app notification to each submitter.
      // This is what makes "sent" mean something: the submitter now has a
      // notification in their NotificationCenter linking to My Submissions.
      const deliveries = pending
        .filter(p => p.submitterId != null)
        .map(p =>
          sendNotification({
            event: 'custom',
            channels: ['in_app'],
            recipientIds: [p.submitterId!],
            title: 'Prioritization outcome for your idea',
            body: p.outcomeSummary,
            priority: 'normal',
            data: {
              kind: 'stakeholder_pulse',
              pulseId: p.id,
              itemId: p.itemId,
              prioritySetId: p.prioritySetId,
              outcome: p.outcome,
            },
            actionUrl: '/prioritization/intake/mine',
            actionLabel: 'View outcome & respond',
          }).catch(err =>
            console.warn(`[StakeholderPulseStore] Notification delivery failed for pulse ${p.id}:`, err),
          ),
        );

      // Fire deliveries concurrently — they are non-blocking relative to the
      // DB write that already succeeded above.
      await Promise.allSettled(deliveries);

      set(state => ({
        pulses: state.pulses.map(p =>
          pulseIds.includes(p.id) && p.status === 'pending'
            ? { ...p, status: 'sent', sentAt: new Date(now) }
            : p,
        ),
      }));

      return { sentCount: pending.length };
    } catch (err) {
      return { sentCount: 0, error: String(err) };
    }
  },

  markViewed: async (pulseId) => {
    if (!isSupabaseAvailable()) return;

    const pulse = get().getPulseById(pulseId);
    if (!pulse || pulse.viewedAt) return; // Already viewed

    try {
      const now = new Date().toISOString();
      await getSafeSupabaseClient()!
        .from(TABLE)
        .update({ status: 'viewed', viewed_at: now })
        .eq('id', pulseId)
        .eq('status', 'sent');

      set(state => ({
        pulses: state.pulses.map(p =>
          p.id === pulseId
            ? { ...p, status: 'viewed' as const, viewedAt: new Date(now) }
            : p,
        ),
      }));
    } catch (err) {
      console.error('[StakeholderPulseStore] markViewed failed:', err);
    }
  },

  recordResponse: async (pulseId, response, responseNote) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Supabase unavailable' };

    try {
      const now = new Date().toISOString();
      const { error } = await getSafeSupabaseClient()!
        .from(TABLE)
        .update({
          status: 'responded',
          responded_at: now,
          response,
          response_note: responseNote ?? null,
        })
        .eq('id', pulseId);

      if (error) throw error;

      set(state => ({
        pulses: state.pulses.map(p =>
          p.id === pulseId
            ? {
                ...p,
                status: 'responded' as const,
                respondedAt: new Date(now),
                response,
                responseNote: responseNote ?? null,
              }
            : p,
        ),
      }));

      return { success: true };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  },

  // ── Queries ───────────────────────────────────────────────────────────────

  getPulseById: (id) => get().pulses.find(p => p.id === id),

  getPulsesForPrioritySet: (prioritySetId) =>
    get().pulses.filter(p => p.prioritySetId === prioritySetId),

  getPulsesForSubmitter: (submitterId) =>
    get().pulses.filter(p => p.submitterId === submitterId),

  getPulsesForItem: (itemId) =>
    get().pulses.filter(p => p.itemId === itemId),

  getSignalForPrioritySet: (prioritySetId) => {
    const pulses = get().getPulsesForPrioritySet(prioritySetId);
    const total = pulses.length;
    const sent = pulses.filter(p => p.status !== 'pending').length;
    const responded = pulses.filter(p => p.status === 'responded').length;
    const addressed = pulses.filter(p => p.response === 'addressed').length;
    const partiallyAddressed = pulses.filter(p => p.response === 'partially_addressed').length;
    const notAddressed = pulses.filter(p => p.response === 'not_addressed').length;

    const responseRate = sent > 0 ? responded / sent : 0;
    const addressedRate = responded > 0 ? addressed / responded : 0;
    const qualityScore =
      responded > 0
        ? (addressed * 1 + partiallyAddressed * 0.5) / responded
        : 0;

    return {
      total,
      sent,
      responded,
      addressed,
      partiallyAddressed,
      notAddressed,
      responseRate,
      addressedRate,
      qualityScore,
    };
  },
}));

// ── Enrichment helper (for UI — merges item/user/priority-set display data) ──

export function enrichPulse(
  pulse: StakeholderPulse,
  opts: {
    items?: Array<{ id: string; title: string }>;
    users?: Array<{ id: string; name?: string; email?: string }>;
    prioritySets?: Array<{ id: string; name: string }>;
  },
): StakeholderPulseWithDetails {
  const item = opts.items?.find(i => i.id === pulse.itemId);
  const user = opts.users?.find(u => u.id === pulse.submitterId);
  const ps = opts.prioritySets?.find(ps => ps.id === pulse.prioritySetId);
  return {
    ...pulse,
    itemTitle: item?.title,
    submitterName: user?.name ?? user?.email,
    prioritySetName: ps?.name,
  };
}
